/**
 * useFiasAIAgentChat — the agent-mode chat engine behind the GLOBAL Fias AI
 * window (evolution of useFiasAIChat; that hook keeps serving the legacy
 * single-shot path until the migration completes).
 *
 * Differences from the legacy hook:
 * - Talks to the orchestrator (`api.assistantTurn`) — multi-iteration agent
 *   loop with server tools; history is server-loaded, never sent.
 * - Renders an activity feed (assistant_tool_call events) inside the
 *   assistant message.
 * - Handles the three pause kinds: clarifying question (answer via the
 *   normal input), explicit confirmation (Approve/Reject card →
 *   respondToConfirmation), and UI actions (auto-applied against the
 *   active arche's capabilities, outcome posted to /action-result; the
 *   server resumes the turn on a fresh requestId).
 * - Every requestId gets its own assistant message; a resumed turn appends
 *   a new assistant bubble under the same conversation.
 *
 * Capabilities come from a getter (the provider's arche registry), read at
 * event time — never captured at submit time — so navigation mid-turn is
 * handled: a UI action targeting a no-longer-open arche is reported back
 * as failed and rendered as a stale event, never applied elsewhere.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { addBreadcrumb, captureError } from '@/lib/telemetry';
import {
  getSelectedVaultHolderId,
  subscribeSelectedVaultHolder,
} from '@/components/HolderSwitcher';
import { toast } from '@/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { api, WEBSOCKET_ENDPOINT } from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { useStreaming } from '@/lib/hooks/useStreaming';
import type {
  AssistantToolCallEventPayload,
  AssistantQuestionEventPayload,
  AssistantConfirmationEventPayload,
  AssistantUiActionEventPayload,
  AssistantNavigateEventPayload,
} from '@/lib/websocket';
import type { ArcheAICapabilities } from '../types';
import { applyAiWrites } from '../lib/aiDataSync';
import { applyUiActionWithTimeout } from '../lib/applyUiAction';
import type {
  AgentChatMessage,
  AgentEvent,
  AssistantBusyState,
  AssistantConfirmationPreview,
} from '../agent-types';

interface UseFiasAIAgentChatOptions {
  /** Resolve the currently-open arche's capabilities (null on non-arche pages). */
  getCapabilities: () => ArcheAICapabilities | null;
  /**
   * Navigate the app to an arche (assistant open_arche tool). The provider
   * wires react-router's navigate; prefill rides location.state.fiasAiPrefill
   * for arches that support intake. Re-validate archeId here — the payload
   * crossed the wire.
   */
  onNavigate?: (archeId: string, prefill?: Record<string, unknown>, path?: string) => void;
}

export interface PendingQuestion {
  interactionId: string;
  question: string;
  choices?: string[];
}

export interface PendingConfirmation {
  interactionId: string;
  toolName: string;
  riskTier: string;
  reason: string;
  preview: AssistantConfirmationPreview;
}

export interface UseFiasAIAgentChatReturn {
  messages: AgentChatMessage[];
  conversationId: string | null;
  isStreaming: boolean;
  busyState: AssistantBusyState;
  /** Cooperative cancel of the in-flight turn (server stops between steps). */
  stopTurn: () => Promise<void>;
  currentModel: {
    provider: string;
    modelName: string;
    displayName: string;
    tier: string;
  } | null;
  pendingQuestion: PendingQuestion | null;
  pendingConfirmation: PendingConfirmation | null;
  submit: (
    message: string,
    options?: { origin?: 'make_a_request' | 'assistant'; archeSearchEnabled?: boolean },
  ) => Promise<void>;
  /** Bind the engine to a conversation created elsewhere (MaR search-only save). */
  adoptConversation: (conversationId: string) => void;
  respondToConfirmation: (
    approved: boolean,
    feedback?: string,
    edits?: Record<string, string>,
  ) => Promise<void>;
  stop: () => void;
  reset: () => void;
  /**
   * Replace the thread wholesale — conversation-history restore (sidebar)
   * and clears. Resets the session and any pending interactions; the next
   * turn continues `conversationId` server-side.
   */
  replaceMessages: (messages: AgentChatMessage[], conversationId: string | null) => void;
  /** Append client-only messages (MaR search-only exchanges). */
  appendMessages: (messages: AgentChatMessage[]) => void;
  /** Temporary (incognito) chat — no memory reads, no memory writes. */
  temporaryChat: boolean;
  /** ADMIN-ONLY verbose debug stream (arche-builder contract). */
  verboseMode: boolean;
  setVerboseMode: (v: boolean) => void;
  verboseEvents: Array<{ type: string; timestamp: string; data: unknown }>;
  /** Load a past conversation (shared MaR history) into the window. */
  loadConversation: (conversationId: string) => Promise<void>;
  setTemporaryChat: (v: boolean) => void;
}

let eventSeq = 0;
const nextEventId = () => `evt-${++eventSeq}`;

export function useFiasAIAgentChat({
  getCapabilities,
  onNavigate,
}: UseFiasAIAgentChatOptions): UseFiasAIAgentChatReturn {
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<AgentChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentModel, setCurrentModel] = useState<UseFiasAIAgentChatReturn['currentModel']>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [currentAssistantMessageId, setCurrentAssistantMessageId] = useState<string | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<PendingQuestion | null>(null);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [temporaryChat, setTemporaryChat] = useState(false);
  const [verboseMode, setVerboseMode] = useState(false);
  const [verboseEvents, setVerboseEvents] = useState<
    Array<{ type: string; timestamp: string; data: unknown }>
  >([]);
  const verboseModeRef = useRef(false);
  verboseModeRef.current = verboseMode;
  const pushVerboseEvent = useCallback((type: string, data: unknown) => {
    if (!verboseModeRef.current) return;
    setVerboseEvents((prev) => [
      ...prev.slice(-199),
      { type, timestamp: new Date().toISOString(), data },
    ]);
  }, []);

  const authToken = authApi.getIdToken();

  // Refs for values used inside stable streaming callbacks.
  const getCapabilitiesRef = useRef(getCapabilities);
  getCapabilitiesRef.current = getCapabilities;
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;
  const assistantMessageIdRef = useRef(currentAssistantMessageId);
  assistantMessageIdRef.current = currentAssistantMessageId;
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  // The workspace selection this conversation's server session was pinned to
  // (captured on the first turn). A live session's acting Holder is immutable
  // server-side, so a later switch to a different workspace must start a fresh
  // conversation rather than silently keep acting in this one. Null = personal
  // (no explicit selection) or no session yet.
  const conversationHolderRef = useRef<string | null>(null);
  // toolCallIds whose ui_action has already been applied this session — the
  // dedup key shared by the live broadcast path and the durable-state
  // reconcile path, so a ui_action can never be applied twice.
  const handledUiToolCallIdsRef = useRef<Set<string>>(new Set());

  const appendEvent = useCallback((event: AgentEvent) => {
    const msgId = assistantMessageIdRef.current;
    if (!msgId) return;
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === msgId ? { ...msg, events: [...(msg.events ?? []), event] } : msg,
      ),
    );
  }, []);

  /**
   * Settle a tool row: patch the matching row in the CURRENT assistant bubble,
   * or append it when there is nothing to patch.
   *
   * The append branch is what the confirmation-resume path needs. An approved
   * tool executes in a NEW turn, so its `running`/`awaiting` row lives in the
   * PREVIOUS bubble and a patch scoped to the current one matches nothing —
   * the executed row used to disappear entirely, leaving the earlier row parked
   * on `awaiting` with no visible outcome even though the write had committed.
   */
  const settleToolEvent = useCallback((event: Extract<AgentEvent, { kind: 'tool' }>) => {
    const msgId = assistantMessageIdRef.current;
    if (!msgId) return;
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== msgId) return msg;
        const events = msg.events ?? [];
        const existing = events.some((e) => e.kind === 'tool' && e.toolCallId === event.toolCallId);
        return {
          ...msg,
          events: existing
            ? events.map((e) =>
                e.kind === 'tool' && e.toolCallId === event.toolCallId
                  ? { ...e, status: event.status, summary: event.summary, refs: event.refs }
                  : e,
              )
            : [...events, event],
        };
      }),
    );
  }, []);

  // ---- Assistant agent-loop event handlers (stable via refs) --------------

  const handleToolCall = useCallback(
    (payload: AssistantToolCallEventPayload) => {
      addBreadcrumb('fias-ai', `tool ${payload.status}`, {
        tool: payload.toolName,
        toolCallId: payload.toolCallId,
        riskTier: payload.riskTier,
      });
      if (payload.status === 'running') {
        appendEvent({
          kind: 'tool',
          id: nextEventId(),
          toolCallId: payload.toolCallId,
          toolName: payload.toolName,
          description: payload.description,
          riskTier: payload.riskTier,
          status: 'running',
        });
      } else {
        settleToolEvent({
          kind: 'tool',
          id: nextEventId(),
          toolCallId: payload.toolCallId,
          toolName: payload.toolName,
          description: payload.description,
          riskTier: payload.riskTier,
          status: payload.status,
          summary: payload.summary,
          refs: payload.refs,
        });
        if (payload.status === 'done' && payload.domains && payload.domains.length > 0) {
          applyAiWrites(queryClient, payload.domains, getCapabilitiesRef.current());
        }
      }
    },
    [appendEvent, settleToolEvent, queryClient],
  );

  const handleQuestion = useCallback(
    (payload: AssistantQuestionEventPayload) => {
      addBreadcrumb('fias-ai', 'question', { interactionId: payload.interactionId });
      appendEvent({
        kind: 'clarification',
        id: nextEventId(),
        interactionId: payload.interactionId,
        question: payload.question,
        choices: payload.choices,
      });
      setPendingQuestion({
        interactionId: payload.interactionId,
        question: payload.question,
        choices: payload.choices,
      });
    },
    [appendEvent],
  );

  const handleConfirmation = useCallback(
    (payload: AssistantConfirmationEventPayload) => {
      addBreadcrumb('fias-ai', 'confirmation required', {
        tool: payload.toolName,
        riskTier: payload.riskTier,
        reason: payload.reason,
        interactionId: payload.interactionId,
      });
      appendEvent({
        kind: 'confirmation',
        id: nextEventId(),
        interactionId: payload.interactionId,
        toolName: payload.toolName,
        riskTier: payload.riskTier,
        reason: payload.reason,
        preview: payload.preview,
        status: 'pending',
      });
      setPendingConfirmation({
        interactionId: payload.interactionId,
        toolName: payload.toolName,
        riskTier: payload.riskTier,
        reason: payload.reason,
        preview: payload.preview,
      });
    },
    [appendEvent],
  );

  const handleUiAction = useCallback(
    (payload: AssistantUiActionEventPayload) => {
      const caps = getCapabilitiesRef.current();
      const sid = sessionIdRef.current;
      if (!sid) return;

      // Dedup: the live WS broadcast and the durable-state reconcile can both
      // surface the same ui_action (by design — the reconcile is the safety
      // net for a dropped broadcast). Apply each toolCallId at most once.
      if (handledUiToolCallIdsRef.current.has(payload.toolCallId)) return;
      handledUiToolCallIdsRef.current.add(payload.toolCallId);

      /**
       * Report the outcome. A refusal to dispatch travels as a structured
       * `code` (+ the arche that was open instead); the SERVER composes what
       * the model is told about it. `error` is reserved for the arche's own
       * failure text when applyAction threw.
       */
      const finish = async (
        ok: boolean,
        error?: string,
        refusal?: { code: 'ARCHE_NOT_OPEN' | 'ARCHE_MISMATCH'; openArcheId?: string },
      ) => {
        appendEvent({
          kind: 'ui_action',
          id: nextEventId(),
          toolCallId: payload.toolCallId,
          actionId: payload.actionId,
          description: payload.description,
          status: ok ? 'applied' : caps && caps.archeId === payload.archeId ? 'failed' : 'stale',
          targetArcheId: payload.archeId,
        });
        try {
          const res = await api.assistantActionResult({
            sessionId: sid,
            toolCallId: payload.toolCallId,
            ok,
            error,
            code: refusal?.code,
            openArcheId: refusal?.openArcheId,
            archeContext: caps
              ? {
                  archeId: caps.archeId,
                  description: caps.archeDescription,
                  state: caps.getStateSnapshot(),
                  actions: caps.actions,
                }
              : undefined,
          });
          if (res.resumed && res.requestId) {
            startAssistantBubbleRef.current(res.requestId);
            // Durable-state reconcile: the resumed turn may emit its next
            // ui_action faster than we can subscribe to the new requestId,
            // in which case the live broadcast is dropped (server's
            // waitForSubscribers gives up after ~600ms). The pause is durable,
            // so read it back and drive it through the same path. No-op when
            // the live broadcast already delivered (deduped by toolCallId).
            void reconcileResumedUiActionRef.current(sid, res.requestId);
          } else {
            setIsStreaming(false);
          }
        } catch (err) {
          captureError(err instanceof Error ? err : new Error(String(err)), {
            source: 'useFiasAIAgentChat.assistantActionResult',
          });
          setIsStreaming(false);
        }
      };

      if (!caps) {
        void finish(false, undefined, { code: 'ARCHE_NOT_OPEN' });
        return;
      }
      // The action is bound to the arche it was proposed for — the same
      // action id can exist on a different arche after navigation. Refuse
      // on mismatch; the server tells the model what happened and how to
      // recover (it knows both arches — the client only reports the fact).
      if (caps.archeId !== payload.archeId) {
        void finish(false, undefined, { code: 'ARCHE_MISMATCH', openArcheId: caps.archeId });
        return;
      }
      // applyAction may be async (a plugin arche relays the action into its
      // sandboxed iframe and waits for the result) — await it, bounded by a
      // timeout, before reporting the outcome to the model. Contract +
      // rationale live with applyUiActionWithTimeout.
      void applyUiActionWithTimeout(
        (actionId, params) => caps.applyAction(actionId, params),
        payload.actionId,
        payload.params,
      ).then((result) => finish(result.ok, result.error));
    },
    [appendEvent],
  );
  // Ref indirection breaks the handleUiAction ⇄ reconcile cycle (reconcile
  // drives handleUiAction; handleUiAction schedules reconcile after a resume).
  const handleUiActionRef = useRef(handleUiAction);
  handleUiActionRef.current = handleUiAction;

  /**
   * Reconcile the session's DURABLE pending ui_action after a resume handoff.
   * The live `assistant_ui_action` broadcast is best-effort: if the resumed
   * turn pauses before the client subscribes to the new requestId, the server
   * broadcasts to zero subscribers and the event is lost, stranding the turn.
   * The pause is persisted, so we read it back and drive it through the same
   * handleUiAction path (deduped by toolCallId, so this is a no-op whenever the
   * live broadcast already delivered).
   */
  const reconcileResumedUiAction = useCallback(async (sid: string, requestId: string) => {
    try {
      const state = await api.getAssistantSession(sid);
      const p = state.pendingInteraction;
      if (state.status !== 'awaiting_ui_action' || !p || p.kind !== 'ui_action') return;
      if (!p.toolCallId || handledUiToolCallIdsRef.current.has(p.toolCallId)) return;
      const caps = getCapabilitiesRef.current();
      handleUiActionRef.current({
        requestId,
        toolCallId: p.toolCallId,
        // Prefer the server-persisted target arche; fall back to the open one
        // for pauses persisted before archeId was stored.
        archeId: p.archeId ?? caps?.archeId ?? '',
        actionId: p.actionId ?? '',
        params: (p.params ?? {}) as Record<string, unknown>,
        description:
          caps?.actions.find((a) => a.id === p.actionId)?.description ?? p.actionId ?? '',
      });
    } catch {
      // Best-effort: on any failure the live broadcast path is unaffected.
    }
  }, []);
  const reconcileResumedUiActionRef = useRef(reconcileResumedUiAction);
  reconcileResumedUiActionRef.current = reconcileResumedUiAction;

  const handleNavigate = useCallback((payload: AssistantNavigateEventPayload) => {
    // Defense-in-depth: the tool schema validated server-side; the client
    // re-validates before touching the router. Both platform id shapes —
    // seeded `arche_<slug>` and generated `arc_<32 hex>` (every plugin) —
    // mirroring isValidArcheId in @fias/db-types (not importable here).
    if (!/^(arche_[a-z0-9_]{1,80}|arc_[a-f0-9]{32})$/.test(payload.archeId)) return;
    onNavigateRef.current?.(payload.archeId, payload.prefill, payload.path);
  }, []);

  // ---- Streaming subscription ---------------------------------------------

  const {
    streamingText,
    isComplete: streamingComplete,
    completePayload,
    error: streamingError,
    reset: resetStreaming,
  } = useStreaming({
    requestId: currentRequestId,
    authToken,
    endpoint: WEBSOCKET_ENDPOINT,
    enabled: !!authToken,
    onAssistantToolCall: handleToolCall,
    onSystemPrompt: (prompt: string) => pushVerboseEvent('system_prompt', { prompt }),
    onToolsAvailable: (tools: Array<{ name: string; description: string }>) =>
      pushVerboseEvent('tools_available', { tools }),
    onToolCall: (toolName: string, toolInput: unknown) =>
      pushVerboseEvent('tool_call', { toolName, toolInput }),
    onToolResult: (toolName: string, toolResult: unknown, isError: boolean) =>
      pushVerboseEvent('tool_result', { toolName, toolResult, isError }),
    onAssistantQuestion: handleQuestion,
    onAssistantConfirmation: handleConfirmation,
    onAssistantUiAction: handleUiAction,
    onAssistantNavigate: handleNavigate,
  });

  const currentModelRef = useRef(currentModel);
  currentModelRef.current = currentModel;

  /** Begin a new assistant bubble bound to a (fresh or resumed) requestId. */
  const startAssistantBubble = useCallback(
    (requestId: string) => {
      const id = `assistant-${Date.now()}-${requestId.slice(0, 6)}`;
      setMessages((prev) => [
        ...prev,
        {
          id,
          role: 'assistant',
          content: '',
          isStreaming: true,
          events: [],
          modelInfo: currentModelRef.current,
        },
      ]);
      resetStreaming();
      setCurrentAssistantMessageId(id);
      assistantMessageIdRef.current = id;
      setCurrentRequestId(requestId);
      setIsStreaming(true);
    },
    [resetStreaming],
  );
  const startAssistantBubbleRef = useRef(startAssistantBubble);
  startAssistantBubbleRef.current = startAssistantBubble;

  const combinedStreamingText = useMemo(
    () => Object.values(streamingText).join(''),
    [streamingText],
  );

  useEffect(() => {
    if (currentAssistantMessageId && combinedStreamingText) {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === currentAssistantMessageId ? { ...msg, content: combinedStreamingText } : msg,
        ),
      );
    }
  }, [currentAssistantMessageId, combinedStreamingText]);

  // Turn completion. A pause (`paused` on the complete payload) keeps the
  // conversation in a waiting state — the pending card drives the resume.
  useEffect(() => {
    if (!streamingComplete || !currentAssistantMessageId) return;
    const paused = (completePayload as { paused?: string } | null)?.paused;
    addBreadcrumb('fias-ai', paused ? `turn paused (${paused})` : 'turn complete', {
      requestId: currentRequestId,
    });
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== currentAssistantMessageId) return msg;
        return {
          ...msg,
          isStreaming: false,
          // Defensive: a turn must never leave a tool row spinning. The
          // confirmation-pause path already transitions the paused tool to
          // 'awaiting' server-side; this sweeps any other orphaned 'running'
          // event (the tool never reported a terminal status) so the spinner
          // can't hang past the end of the turn.
          events: msg.events?.map((e) =>
            e.kind === 'tool' && e.status === 'running'
              ? { ...e, status: 'error' as const, summary: e.summary ?? 'Interrupted' }
              : e,
          ),
        };
      }),
    );
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();
    if (!paused || paused === 'ui_action') {
      // ui_action pauses resolve via handleUiAction → action-result resume;
      // isStreaming stays true only while that round-trip is in flight.
      if (!paused) setIsStreaming(false);
    } else {
      setIsStreaming(false);
    }
  }, [
    streamingComplete,
    completePayload,
    currentAssistantMessageId,
    currentRequestId,
    resetStreaming,
  ]);

  useEffect(() => {
    if (!streamingError || !currentAssistantMessageId) return;
    addBreadcrumb('fias-ai', 'turn error', {
      requestId: currentRequestId,
      code: streamingError.code,
      message: streamingError.message.slice(0, 200),
    });
    const isAuthError =
      streamingError.message.toLowerCase().includes('unauthorized') ||
      streamingError.code === 'UNAUTHORIZED';
    const errorMessage = isAuthError
      ? 'Your session has expired. Please log out and log back in to continue.'
      : `Error: ${streamingError.message}`;
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === currentAssistantMessageId
          ? { ...msg, content: msg.content || errorMessage, isStreaming: false }
          : msg,
      ),
    );
    setIsStreaming(false);
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    setPendingQuestion(null);
    setPendingConfirmation(null);
    resetStreaming();
  }, [streamingError, currentAssistantMessageId, currentRequestId, resetStreaming]);

  // ---- User entry points ---------------------------------------------------

  const submit = useCallback(
    async (
      message: string,
      options?: { origin?: 'make_a_request' | 'assistant'; archeSearchEnabled?: boolean },
    ) => {
      const trimmed = message.trim();
      if (!trimmed || (isStreaming && !pendingQuestion)) return;

      const answeringQuestion = pendingQuestion !== null;
      if (answeringQuestion) {
        setMessages((prev) =>
          prev.map((msg) => ({
            ...msg,
            events: msg.events?.map((e) =>
              e.kind === 'clarification' && e.interactionId === pendingQuestion.interactionId
                ? { ...e, answer: trimmed }
                : e,
            ),
          })),
        );
        setPendingQuestion(null);
      }

      setMessages((prev) => [
        ...prev,
        { id: `user-${Date.now()}`, role: 'user', content: trimmed },
      ]);
      setVerboseEvents([]);

      const caps = getCapabilitiesRef.current();
      const actingHolder = getSelectedVaultHolderId() ?? null;
      const isFreshSession = sessionId === null;
      try {
        const result = await api.assistantTurn({
          message: trimmed,
          conversationId: conversationId ?? undefined,
          sessionId: sessionId ?? undefined,
          // Act in the workspace the user is looking at (holder switcher).
          // Server-validated + session-pinned; resumes ignore this value.
          activeHolderId: actingHolder ?? undefined,
          routingPreference: 'auto',
          origin: options?.origin,
          toolPreferences:
            options?.archeSearchEnabled === false ? { archeSearch: false } : undefined,
          temporaryChat: temporaryChat || undefined,
          verbose: verboseMode || undefined,
          archeContext: caps
            ? {
                archeId: caps.archeId,
                description: caps.archeDescription,
                state: caps.getStateSnapshot(),
                actions: caps.actions,
              }
            : undefined,
        });
        setConversationId(result.conversationId);
        setSessionId(result.sessionId);
        sessionIdRef.current = result.sessionId;
        // Record the workspace this session pinned to (first turn only) so a
        // later switch can detect the mismatch and start a fresh conversation.
        if (isFreshSession) conversationHolderRef.current = actingHolder;
        setCurrentModel(result.selectedModel);
        pushVerboseEvent('model_info', { ...result.selectedModel, intent: result.intent });
        setPendingConfirmation(null);
        addBreadcrumb('fias-ai', 'turn started', {
          requestId: result.requestId,
          sessionId: result.sessionId,
          tier: result.selectedModel?.tier,
          messageChars: trimmed.length,
        });
        startAssistantBubble(result.requestId);
      } catch (err) {
        captureError(err instanceof Error ? err : new Error(String(err)), {
          source: 'useFiasAIAgentChat.assistantTurn',
        });
        const errorStr = String(err).toLowerCase();
        const isAuthError = errorStr.includes('unauthorized') || errorStr.includes('token');
        setMessages((prev) => [
          ...prev,
          {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: isAuthError
              ? 'Your session has expired. Please log out and log back in to continue.'
              : 'Sorry, something went wrong. Please try again.',
          },
        ]);
        setIsStreaming(false);
      }
    },
    [isStreaming, pendingQuestion, conversationId, sessionId, temporaryChat, startAssistantBubble],
  );

  const respondToConfirmation = useCallback(
    async (approved: boolean, feedback?: string, edits?: Record<string, string>) => {
      const pending = pendingConfirmation;
      const sid = sessionIdRef.current;
      if (!pending || !sid) return;

      setMessages((prev) =>
        prev.map((msg) => ({
          ...msg,
          events: msg.events?.map((e) => {
            if (e.kind !== 'confirmation' || e.interactionId !== pending.interactionId) return e;
            // Fold any in-card edits into the preview so the settled record shows
            // the message as it was actually approved, not the original proposal.
            const preview =
              approved && edits && e.preview.editable
                ? {
                    ...e.preview,
                    editable: e.preview.editable.map((f) =>
                      edits[f.key] !== undefined ? { ...f, value: edits[f.key] } : f,
                    ),
                  }
                : e.preview;
            return {
              ...e,
              preview,
              status: approved ? ('approved' as const) : ('rejected' as const),
            };
          }),
        })),
      );
      setPendingConfirmation(null);

      const caps = getCapabilitiesRef.current();
      try {
        const result = await api.assistantTurn({
          sessionId: sid,
          conversationId: conversationId ?? undefined,
          confirmationResponse: {
            interactionId: pending.interactionId,
            approved,
            feedback,
            // Only meaningful on approval; the server ignores edits on reject.
            ...(approved && edits && Object.keys(edits).length > 0 ? { edits } : {}),
          },
          verbose: verboseModeRef.current || undefined,
          // The resumed turn rebuilds its tool set from scratch, and the open
          // arche's tools are "core" ONLY because archeContext names it (see
          // buildToolList's two-tier catalog). Omitting it here dropped every
          // tool of the arche the user is looking at — 41 of them for email —
          // plus all ui__ actions, so the model came back from an approval it
          // had just executed announcing it had no tools for the job. Send it
          // on resumes exactly as assistantActionResult already does.
          archeContext: caps
            ? {
                archeId: caps.archeId,
                description: caps.archeDescription,
                state: caps.getStateSnapshot(),
                actions: caps.actions,
              }
            : undefined,
        });
        setCurrentModel(result.selectedModel);
        pushVerboseEvent('model_info', result.selectedModel);
        startAssistantBubble(result.requestId);
      } catch (err) {
        captureError(err instanceof Error ? err : new Error(String(err)), {
          source: 'useFiasAIAgentChat.confirmationResponse',
        });
        setMessages((prev) =>
          prev.map((msg) => ({
            ...msg,
            events: msg.events?.map((e) =>
              e.kind === 'confirmation' && e.interactionId === pending.interactionId
                ? { ...e, status: 'expired' as const }
                : e,
            ),
          })),
        );
        setIsStreaming(false);
      }
    },
    [pendingConfirmation, conversationId, startAssistantBubble],
  );

  const stop = useCallback(() => {
    if (!isStreaming && !currentAssistantMessageId) return;
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === currentAssistantMessageId ? { ...msg, isStreaming: false } : msg,
      ),
    );
    setIsStreaming(false);
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();
  }, [isStreaming, currentAssistantMessageId, resetStreaming]);

  const reset = useCallback(() => {
    setMessages([]);
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    setConversationId(null);
    setSessionId(null);
    sessionIdRef.current = null;
    conversationHolderRef.current = null;
    setCurrentModel(null);
    setIsStreaming(false);
    setPendingQuestion(null);
    setPendingConfirmation(null);
    setVerboseEvents([]);
    handledUiToolCallIdsRef.current = new Set();
    resetStreaming();
  }, [resetStreaming]);

  // Mirror the app-wide workspace switch (header switcher or the AI-window
  // chip — both write the same selection) into the conversation. A live
  // session's acting Holder is pinned on its first turn (a security control:
  // a mid-confirmation switch must not redirect an approved action), so a
  // switch can't retarget an existing conversation — continuing it would keep
  // acting in the old workspace. Start a fresh chat so the next turn pins to
  // the newly-selected workspace. No live session ⇒ nothing to reset (it pins
  // on its next turn). Personal-only Vault data (email, contacts, calendar)
  // always uses the Personal Holder regardless — see the executor's
  // applyPersonalHolderScope — so this only affects org-capable data.
  useEffect(() => {
    return subscribeSelectedVaultHolder((next) => {
      if (!sessionIdRef.current) return;
      // Normalize against the personal holder id: a null selection ("no
      // explicit workspace") resolves server-side to the Personal Holder, so
      // null and the personal id are the SAME workspace. Without this the
      // app's default-to-personal write (null → personalId on first load)
      // reads as a switch and wrongly wipes a live conversation. Read from the
      // already-populated memberships cache (the header chip's query).
      const personalId =
        (
          queryClient.getQueryData(queryKeys.vault.me.memberships()) as
            | { personalHolder?: { holderId?: string } }
            | undefined
        )?.personalHolder?.holderId ?? null;
      const resolve = (h: string | null) => h ?? personalId;
      if (resolve(next) === resolve(conversationHolderRef.current)) return;
      reset();
      toast({
        title: 'Workspace switched',
        description: 'Started a new Fias AI chat so it acts in the workspace you just selected.',
      });
    });
  }, [reset, queryClient]);

  const replaceMessages = useCallback(
    (msgs: AgentChatMessage[], convId: string | null) => {
      setMessages(msgs);
      setConversationId(convId);
      setSessionId(null);
      sessionIdRef.current = null;
      conversationHolderRef.current = null;
      setCurrentRequestId(null);
      setCurrentAssistantMessageId(null);
      setPendingQuestion(null);
      setPendingConfirmation(null);
      setIsStreaming(false);
      handledUiToolCallIdsRef.current = new Set();
      resetStreaming();
    },
    [resetStreaming],
  );

  /**
   * Load a past conversation (the shared Make-a-Request history) into the
   * window. Fresh session — the next message continues that thread.
   */
  const loadConversation = useCallback(
    async (convId: string) => {
      const conv = await api.getConversation(convId);
      const msgs = (conv?.messages ?? [])
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m, i) => {
          const meta = m.metadata as {
            archeSuggestions?: AgentChatMessage['archeSuggestions'];
            settledActions?: Array<{
              title: string;
              status: 'approved' | 'rejected';
              fields: Array<{ label: string; value: string }>;
            }>;
          } | null;
          // Rebuild the settled-confirmation records (persisted server-side) as
          // confirmation events so a reloaded conversation still shows what was
          // approved/rejected — agent events are otherwise ephemeral. Synthetic
          // ids: these are read-only history, never re-actionable.
          const events: AgentEvent[] | undefined = meta?.settledActions?.length
            ? meta.settledActions.map((a, j) => ({
                kind: 'confirmation' as const,
                id: `hist-conf-${convId}-${i}-${j}`,
                interactionId: `hist-conf-${convId}-${i}-${j}`,
                toolName: '',
                riskTier: '',
                reason: '',
                preview: {
                  type: 'generic' as const,
                  title: a.title,
                  fields: a.fields,
                  actingAs: '',
                },
                status: a.status,
              }))
            : undefined;
          return {
            id: `hist-${convId}-${i}`,
            role: m.role as 'user' | 'assistant',
            content: m.content,
            archeSuggestions: meta?.archeSuggestions,
            ...(events ? { events } : {}),
          };
        });
      replaceMessages(msgs, convId);
      setVerboseEvents([]);
    },
    [replaceMessages],
  );

  const appendMessages = useCallback((msgs: AgentChatMessage[]) => {
    setMessages((prev) => [...prev, ...msgs]);
  }, []);

  const adoptConversation = useCallback((convId: string) => {
    // A conversation created OUTSIDE the engine (MaR search-only client
    // persistence) becomes the thread the next AI turn continues —
    // otherwise the orchestrator would split a visible thread into a
    // second server conversation (Phase 3 review, finding 1).
    setConversationId(convId);
  }, []);

  const stopTurn = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid) return;
    addBreadcrumb('fias-ai', 'stop requested', { sessionId: sid });
    // Recover the window UNCONDITIONALLY and FIRST. The server stop is
    // best-effort: a 'running' turn also unwinds via its complete event, but a
    // paused/wedged turn (awaiting_ui_action, or a dropped round-trip) returns
    // 409 NOT_RUNNING — no event is coming, so if we only cleared the local
    // state on success the "working" indicator would hang forever. Clearing
    // here means Stop always stops, whatever the server says.
    const msgId = assistantMessageIdRef.current;
    if (msgId) {
      setMessages((prev) =>
        prev.map((msg) => (msg.id === msgId ? { ...msg, isStreaming: false } : msg)),
      );
    }
    setIsStreaming(false);
    setPendingQuestion(null);
    setPendingConfirmation(null);
    setCurrentRequestId(null);
    setCurrentAssistantMessageId(null);
    resetStreaming();
    try {
      await api.assistantStop(sid);
    } catch {
      // NOT_RUNNING (turn already finished / paused) — the local recovery
      // above already restored the UI; nothing more to do.
    }
  }, [resetStreaming]);

  const busyState: AssistantBusyState = pendingConfirmation
    ? 'awaiting_confirmation'
    : pendingQuestion
      ? 'awaiting_user'
      : isStreaming
        ? 'thinking'
        : 'idle';

  return {
    messages,
    conversationId,
    stopTurn,
    isStreaming,
    busyState,
    currentModel,
    pendingQuestion,
    pendingConfirmation,
    submit,
    respondToConfirmation,
    stop,
    reset,
    replaceMessages,
    appendMessages,
    adoptConversation,
    temporaryChat,
    verboseMode,
    setVerboseMode,
    verboseEvents,
    loadConversation,
    setTemporaryChat,
  };
}
